import * as React from "react";
import * as ReactDOM from "react-dom";
import { Provider } from "react-redux";

import { locale } from "moment";
import { AppInsights } from "applicationinsights-js";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

import { enrichLogsWithUser, enrichLogsWithUserOld } from "@teamprevent/common/logging/appInsightsUserLogEnricher";
import { getConfig } from "@teamprevent/common/getConfig";
import { MediaContextProvider, mediaStyles } from "@teamprevent/common/responsive";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";
import { theme } from "@teamprevent/common";
import { setAuthenticatedUserContext, getApplicationAnalytics } from "@teamprevent/common/logging/appInsightsSetup";

import { loadAllowedProducts } from "./allowedProducts";
import { loadAllowedPhysioOrigins } from "./allowedPhysioOrigins";
import { loadAllowedCovid19Features } from "./allowedCovid19Features";
import { loadCustomerDetails } from "./customerDetails";
import { loadAllowedAssessmentAndWellBeingFeatures } from "./allowedAssessmentAndWellBeingFeatures";
import { loadCustomerSraDepartments } from "./customerSraDepartments";
import { getCurrentUser } from "./auth";
import { App } from "./app";

import "semantic-ui-less/semantic.less";
import "../../Style.Common/index.scss";
import "../Style/index.scss";
import { loadCurrentUserOrgStructureLevelOne } from "./orgStructure/actions";
import { configureStore } from "./store";
import { AppOld } from "./appOld";

import { initializeCurrentLocation } from "redux-little-router";

const useAppInsightsConnectionString = getFeatureToggles()[FeatureToggleNames.UseAppInsightsConnectionString];

if (!useAppInsightsConnectionString) {
    AppInsights.downloadAndSetup({ instrumentationKey: getConfig().InstrumentationKey });
}

// Set default locale
locale("en-gb");

const store = configureStore();
(async () => {
    const loadingCurrentUser = store.dispatch<any>(getCurrentUser());
    const loadingAllowedProducts = store.dispatch<any>(loadAllowedProducts());
    const loadingAllowedPhysioOrigins = store.dispatch<any>(loadAllowedPhysioOrigins());
    const loadingAllowedCovid19Features = store.dispatch<any>(loadAllowedCovid19Features());
    const loadingCustomerDetails = store.dispatch<any>(loadCustomerDetails());
    const loadingAllowedAssessmentAndWellBeingFeatures = store.dispatch<any>(loadAllowedAssessmentAndWellBeingFeatures());
    const loadingCustomerSraDepartments = store.dispatch<any>(loadCustomerSraDepartments());
    await Promise.all([
        loadingCurrentUser,
        loadingAllowedProducts,
        loadingAllowedPhysioOrigins,
        loadingAllowedCovid19Features,
        loadingCustomerDetails,
        loadingAllowedAssessmentAndWellBeingFeatures,
        loadingCustomerSraDepartments
    ]);

    const currentCustomerId = store.getState().currentUser?.currentCustomer?.id;
    store.dispatch<any>(loadCurrentUserOrgStructureLevelOne(currentCustomerId));

    const initialLocation = store.getState().router;
    store.dispatch(initializeCurrentLocation(initialLocation));

    if (!useAppInsightsConnectionString) {
        AppInsights.setAuthenticatedUserContext(store.getState().currentUser.id);
        enrichLogsWithUserOld(store);
    } else {
        const applicationAnalytics = getApplicationAnalytics();
        setAuthenticatedUserContext(store.getState().currentUser.id);
        if (applicationAnalytics) {
            enrichLogsWithUser(applicationAnalytics, store);
        }
    }
})();

const enableMuiMigration = getFeatureToggles()[FeatureToggleNames.EnableMuiMigration];

if (enableMuiMigration) {
    ReactDOM.render(
        <>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <style>{mediaStyles}</style>
                <MediaContextProvider>
                    <Provider store={store}>
                        <App />
                    </Provider>
                </MediaContextProvider>
            </ThemeProvider>
        </>,
        document.getElementById("approot")
    );
} else {
    ReactDOM.render(
        <>
            <style>{mediaStyles}</style>
            <MediaContextProvider>
                <Provider store={store}>
                    <AppOld />
                </Provider>
            </MediaContextProvider>
        </>,
        document.getElementById("approot")
    );
}
